/**
 * sentryScrub.ts
 *
 * Lane 0 (docs/designs/observability-and-usage-metering.md, Required
 * Deliverable 1): "allowlist what is sent, not denylist what is stripped."
 * Client-side twin of server/lib/sentryScrub.js — same allowlist decisions,
 * ported to TypeScript for the browser SDK's event shape. Kept as a
 * separate file (not shared across the client/server boundary) because the
 * two run in different module systems and bundlers; the DECISIONS are meant
 * to stay identical, so change both together.
 *
 * scrubEvent() is the beforeSend hook. It builds a brand new event from
 * named-safe fields only, rather than trying to strip known-bad ones from
 * whatever the SDK populated. Anything not listed is dropped by
 * construction. A secondary regex pass (`redactSecrets`) redacts known
 * secret shapes inside the free-text fields that DO survive the allowlist
 * (an interpolated error message can carry a token even though the field
 * itself is "safe").
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

const SECRET_PATTERNS: Array<[RegExp, string]> = [
  [/AKIA[0-9A-Z]{16}/g, '[redacted:aws-access-key]'],
  [/\b[A-Za-z0-9/+]{40}\b/g, '[redacted:base64-40]'],
  [/sk-ant-[A-Za-z0-9_-]{20,}/g, '[redacted:anthropic-key]'],
  [/\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]{16,}\b/g, '[redacted:stripe-key]'],
  [/Bearer\s+[A-Za-z0-9._-]{16,}/gi, 'Bearer [redacted:token]'],
  [/\b((?:api[_-]?key|token|secret|password|passwd)\s*[=:]\s*)[^\s&"']{6,}/gi, '$1[redacted]'],
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g, '[redacted:jwt]'],
  [/\b[a-f0-9]{64}\b/gi, '[redacted:hex64]'],
  // The one-time code/state a connector's consent page sends back to
  // /connectors/callback (lib/urlScrub.ts covers PostHog and Customer.io).
  [/([?&](?:code|state)=)[^&#\s"']+/g, '$1[redacted]'],
];

export function redactSecrets<T>(text: T): T {
  if (typeof text !== 'string' || text.length === 0) return text;
  let out: string = text;
  for (const [pattern, replacement] of SECRET_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out as unknown as T;
}

function scrubExceptionValues(exception: any): any {
  if (!exception || !Array.isArray(exception.values)) return undefined;
  return {
    values: exception.values.map((v: any) => ({
      type: v.type,
      value: redactSecrets(v.value),
      // Never allowlisted: `context_line`/`pre_context`/`post_context` are
      // literal source text read off disk at report time (here: the app's
      // own bundled source via the source map, once one is wired up) — kept
      // structurally identical to the server-side decision even though the
      // risk profile differs (this is our own frontend code, not customer
      // code), for one consistent policy across the whole stack.
      stacktrace: v.stacktrace && Array.isArray(v.stacktrace.frames)
        ? {
          frames: v.stacktrace.frames.map((f: any) => ({
            filename: f.filename,
            function: f.function,
            lineno: f.lineno,
            colno: f.colno,
            in_app: f.in_app,
          })),
        }
        : undefined,
    })),
  };
}

function scrubBreadcrumbs(breadcrumbs: any): any {
  if (!Array.isArray(breadcrumbs)) return undefined;
  return breadcrumbs.map((b: any) => ({
    type: b.type,
    category: b.category,
    level: b.level,
    timestamp: b.timestamp,
    message: redactSecrets(b.message),
    // `data` dropped — the fetch/xhr/console integrations attach request
    // URLs, response bodies and console.log arguments there.
  }));
}

function scrubRequest(request: any): any {
  if (!request || typeof request !== 'object' || typeof request.url !== 'string') return undefined;
  const url = request.url.split('?')[0];
  return { url, headers: undefined };
}

function scrubContexts(contexts: any): any {
  if (!contexts || typeof contexts !== 'object') return undefined;
  const ALLOWED_CONTEXT_KEYS = ['trace', 'runtime', 'browser', 'os', 'app'];
  const out: Record<string, unknown> = {};
  for (const key of ALLOWED_CONTEXT_KEYS) {
    if (contexts[key] !== undefined) out[key] = contexts[key];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function scrubEvent(event: any): any {
  if (!event || typeof event !== 'object') return null;

  const scrubbed: Record<string, unknown> = {
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    level: event.level,
    logger: event.logger,
    transaction: redactSecrets(event.transaction),
    environment: event.environment,
    release: event.release,
    dist: event.dist,
    sdk: event.sdk,
    fingerprint: event.fingerprint,
    // Populated only by our own explicit Sentry.setTag()/setUser() calls —
    // never from raw request/DOM data — so trusted as-is, narrowed
    // defensively in case a future call site widens `user`.
    tags: event.tags,
    user: event.user && typeof event.user === 'object' ? { id: event.user.id } : undefined,
    message: redactSecrets(event.message),
    exception: scrubExceptionValues(event.exception),
    breadcrumbs: scrubBreadcrumbs(event.breadcrumbs),
    request: scrubRequest(event.request),
    contexts: scrubContexts(event.contexts),
    // Deliberately omitted: `extra`, `request.headers`/`cookies`/`data`,
    // `user.email`/`user.ip_address`, DOM element references some browser
    // integrations attach to breadcrumbs.
  };

  for (const key of Object.keys(scrubbed)) {
    if (scrubbed[key] === undefined) delete scrubbed[key];
  }
  return scrubbed;
}
